import { RiskLevel } from '~/types/RiskLevel';
import { ApplicationPayload } from './ApplicationPayload';
import { ApplicationStep } from './ApplicationStep';
import { SoftDeclineError } from './SoftDeclineError';
import { PaymentStagePayload } from './PaymentStagePayload';
import { AgentLead } from './AgentLead';
import { AgentApplication } from './AgentApplication';

type ApplicationLabel = AgentLead['statusKey'] | AgentApplication['statusKey'];

export enum Stage {
  // uae
  fillApplication="fillApplication",
  companyType = 'companyType',
  uaeFillApplication = 'fillApplication',
  highRisk = 'highRisk',
  waitingForScreening = 'waitingForScreening',
  error = 'error',
  screeningError = 'screeningError',
  contract = 'contract',
  payment = 'payment',
  screeningReject = 'screeningReject',
  submit = 'submit',
  successfulOnboarding = 'successfulOnboarding',
  salesforceReject = 'salesforceReject',
  salesforceSoftDecline = 'salesforceSoftDecline',
  salesforceHardDecline = 'salesforceHardDecline',
  salesforceTechnicalError = 'salesforceTechnicalError',
  salesforceError = 'salesforceError',
  terminated = 'terminated',
  completed = 'completed',

  // KSA
  ksaApplication = 'ksaApplication',
  ksaReview = 'ksaReview',
  ksaReviewSuccess = 'ksaReviewSuccess',
  ksaReviewResubmit = 'ksaReviewResubmit',
  ksaReviewReject = 'ksaReviewReject',
  ksaTerminated = 'ksaTerminated',
  ksaServices = 'ksaServices',
  ksaContract = 'ksaContract',
  ksaPayment = 'ksaPayment',
  ksaAutomatedOnboarding = 'ksaAutomatedOnboarding',
  ksaTechFailedOnboarding = 'ksaTechFailedOnboarding',
  ksaFailedOnboarding = 'ksaFailedOnboarding',
  ksaManualOnboarding = 'ksaManualOnboarding',
  ksaCompletedOnboarding = 'ksaCompletedOnboarding',
  ksaCompletion = 'ksaCompletion', // fake stage
}

type ApplicationBase<S, StagePayload = undefined> = {
  uid: string;
  label: ApplicationLabel;
  processUid: string;
  stage: S;
  actualStage?: Stage;
  payload: ApplicationPayload;
  riskLevel?: RiskLevel;
  stagePayload: StagePayload;
  readOnly: boolean;
  errorKeys?: string[];
  errorInformation?: {
    errorFields?: { [namePath: string]: string | null }[];
    errorMessages: string[];
    type: 'ContractStagePayload';
  };
  disablePaymentStep?: boolean;
  terminationReason?: string;
};

export type KsaOnboarding = Array<{
  flowType: string;
  companyId: string;
  applicationId: string;
}>;

export type WizardStagePayload = {
  activeStep: ApplicationStep;
  steps: ApplicationStep[];
  type: 'wizard' | 'agent-wizard';
};

export type Application =
  | ApplicationBase<Stage.companyType>
  | ApplicationBase<Stage.uaeFillApplication, WizardStagePayload>
  | ApplicationBase<
      Stage.terminated,
      {
        activeStep: ApplicationStep;
        steps: ApplicationStep[];
        type: 'wizard' | 'agent-wizard';
        terminationInfo: {
          terminationDate: string;
          terminationReason: string;
        };
      }
    >
  | ApplicationBase<Stage.highRisk>
  | ApplicationBase<Stage.waitingForScreening>
  | ApplicationBase<Stage.error>
  | ApplicationBase<Stage.screeningError>
  | ApplicationBase<
      Stage.contract,
      {
        errorInformation?: {
          errorFields?: { [namePath: string]: string | null }[];
          errorMessages: string[];
          type: 'ContractStagePayload';
        };
      }
    >
  | ApplicationBase<Stage.payment, PaymentStagePayload>
  | ApplicationBase<Stage.screeningReject>
  | ApplicationBase<Stage.submit>
  | ApplicationBase<Stage.successfulOnboarding>
  | ApplicationBase<Stage.salesforceReject>
  | ApplicationBase<
      Stage.salesforceSoftDecline,
      {
        allowToResubmit?: boolean;
        errorsHistory: { created: string; errorMessages: string[] }[];
        errors: SoftDeclineError[];
        type: string;
      }
    >
  | ApplicationBase<Stage.salesforceHardDecline>
  | ApplicationBase<Stage.salesforceTechnicalError>
  | ApplicationBase<Stage.salesforceError>
  | ApplicationBase<Stage.completed>
  | ApplicationBase<Stage.ksaApplication, WizardStagePayload>
  | ApplicationBase<Stage.ksaReview, WizardStagePayload>
  | ApplicationBase<Stage.ksaReviewSuccess, any>
  | ApplicationBase<Stage.ksaReviewResubmit, any>
  | ApplicationBase<Stage.ksaReviewReject, any>
  | ApplicationBase<Stage.ksaTerminated, any>
  | ApplicationBase<Stage.ksaServices, WizardStagePayload>
  | ApplicationBase<Stage.ksaContract, any>
  | ApplicationBase<Stage.ksaPayment, PaymentStagePayload>
  | ApplicationBase<Stage.ksaAutomatedOnboarding, any>
  | ApplicationBase<Stage.ksaTechFailedOnboarding, any>
  | ApplicationBase<Stage.ksaFailedOnboarding, any>
  | ApplicationBase<Stage.ksaManualOnboarding, any>
  | ApplicationBase<Stage.ksaCompletedOnboarding, any>
  | ApplicationBase<Stage.ksaCompletion, any>;
